// pipe: frontend wave (F6) — storefront Cart + Checkout container. Wires the
// cart store, the buyer session, and the Bearer checkout
// (`POST /api/store/checkout` — ONE idempotent server transaction) to the pure
// CartView. All fetch/state/effect plumbing lives HERE.
//
// Checkout flow: an unauthenticated buyer's button label invites sign-in and
// the click starts the PKCE redirect (the cart persists in localStorage across
// it); an authenticated buyer POSTs the checkout with a per-cart-content
// `client_op_id` (see services/cart.ts — a network-failure retry replays the
// same server-side operation; a changed cart mints a fresh identity). On
// success the cart clears and the confirmation renders the receipt — payment
// is `manual_pending` by design (handled offline; no payment SaaS in the data
// path).
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStorefrontAuth } from '../auth/storefront-auth-provider';
import { cartKey, clearOpId, ensureOpId } from '../services/cart';
import { useCart } from '../services/cart-provider';
import type { CheckoutReceipt } from '../services/storefront-api';
import { useStorefrontApi } from '../services/storefront-api-provider';
import { CartView } from './cart-view';

export function Cart() {
  const api = useStorefrontApi();
  const { token, login } = useStorefrontAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<CheckoutReceipt | null>(null);

  const onCheckout = useCallback(() => {
    if (!token) {
      // Not signed in: the PKCE redirect (the cart survives in localStorage).
      void login();
      return;
    }
    if (cart.lines.length === 0 || busy) {
      return;
    }
    const ids = cart.lines.map(l => l.id);
    const opId = ensureOpId(cartKey(cart.lines));
    setBusy(true);
    setCheckoutError(null);
    api
      .checkout(opId, ids)
      .then(r => {
        clearOpId();
        cart.clear();
        setReceipt(r);
      })
      .catch((e: unknown) => {
        // A 409 = stock lost / listing no longer active; keep the op id so an
        // ambiguous network failure can replay, but a definitive 409 is a new
        // situation — mint fresh next time.
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.endsWith(': 409')) {
          clearOpId();
        }
        setCheckoutError(msg);
      })
      .finally(() => {
        setBusy(false);
      });
  }, [api, busy, cart, login, token]);

  const onViewOrders = useCallback(() => navigate('/orders'), [navigate]);

  return (
    <CartView
      lines={cart.lines}
      authed={Boolean(token)}
      busy={busy}
      checkoutError={checkoutError}
      receipt={receipt}
      onRemove={cart.remove}
      onCheckout={onCheckout}
      onViewOrders={onViewOrders}
    />
  );
}
